import { flattenNode, splitNodeAt } from '@common/html/util/dom';

/**
 * Normalizes page breaks in HTML by moving the page breaks to be direct children of the body element.
 * An element with `page-break-after: always` or `page-break-before: always` is considered a page break.
 * If a page break element is not a direct child of the body element, then the page break's ancestors will be split into an element before and after the page break.
 * e.g.
 * ```html
 * <p>
 *   foo
 *   <br style="page-break-after: always">
 *   bar
 * </p>
 * ```
 * will become
 * ```html
 * <p>foo</p>
 * <br style="page-break-after: always">
 * <p>bar</p>
 * ```
 */
export class HtmlPageBreakNormalizer {
  /**
   * Normalizes the HTML.
   * @param doc The HTML doc to normalize.
   */
  normalize(doc: Document, options?: { tagName: string }) {
    // Grab all elements so they can be traversed linearly
    const body = doc.querySelector('body');

    // Split all page breaks that are not direct children of the body element
    flattenNode(body).forEach((element: HTMLElement) => {
      if (element.style?.['page-break-after'] === 'always' || element.style?.['page-break-before'] === 'always') {
        splitNodeAt(element, body);

        // Replace the page break with a different tag if one was provided
        if (options?.tagName) {
          element.replaceWith(doc.createElement(options.tagName));
        }
      }
    });
  }
}
